import { useContext, useEffect, useMemo, useState } from "react";
import styles from "../BundleBuilder.module.css";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import {
    Box,
    Button,
    Collapsible,
    Divider,
    FormLayout,
    Icon,
    InlineGrid,
    Label,
    Link,
    Spinner,
    Tabs,
    Text,
    TextField,
    Thumbnail,
    Tooltip,
    BlockStack,
    InlineStack,
    Card,
    List,
    Banner,
    LegacyCard,
} from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { AiOutlineAlignLeft, AiOutlineAlignCenter, AiOutlineAlignRight } from "react-icons/ai";
import {
    ClipboardIcon,
    CollectionIcon,
    DeleteIcon,
    EditIcon,
    InfoIcon,
    ProductIcon,
    SearchIcon,
} from "@shopify/polaris-icons";
import {
    AvailableBundleBuilderTemplates,
    DefaultWidgetPositions,
    getProductsDynamicallyAndReturnNewProductsArray,
    handleCopyText,
} from "../../constants";
import { ProductObj, StepsType, VariantsObj } from "../../Types";
import { CloseIcon } from "../BundleBuilderBundle/BundleBuilderBundle";
import useBundleBuilderMixpanelHook from "../hooks/useBundleBuilderMixpanelHook";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import TextEditorCart from "../../../../components/TextEditorCart/TextEditorCart";
import { getAppBlockDeepLink } from "../../CommonFields/WidgetPositionsSelector";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import BundleEmbed from "../../../../components/BundleEmbed/BundleEmbed";
import RenderPages from "../../../../components/RenderPages/RenderPages";
import { isDispensaryCoStore, isLilSweetTreatStore } from "../../../constants";
import MantleDoc, { mantleDocHandles } from "../../../../components/MantleDoc";
import { useFormContext, useWatch } from "react-hook-form";
import {
    FormCheckbox,
    FormRadioButton,
    FormSelect,
    FormSettingRow,
    FormTextField,
    FormToggle,
    useVariantFieldDisabled,
} from "../../../../components/FormFields";

const BundleBuilderAdvanced = ({ bundleType }: { bundleType: string }) => {
    const [expandBundleVisibility, setExpandBundleVisibility] = useState(false);
    const [expandProductSettings, setExpandProductSettings] = useState(false);
    const [expandCheckoutSettings, setExpandCheckoutSettings] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);

    const tabs = [
        { id: "visibility", content: "Visibility", accessibilityLabel: "Visibility", panelID: "visibility-panel" },
        { id: "pricing", content: "Pricing", accessibilityLabel: "Pricing", panelID: "pricing-panel" },
        { id: "selection", content: "Selection", accessibilityLabel: "Selection", panelID: "selection-panel" },
        { id: "flow-rules", content: "Flow & Rules", accessibilityLabel: "Flow & Rules", panelID: "flow-rules-panel" },
    ];

    const handleTabChange = (selectedTabIndex: number) => setSelectedTab(selectedTabIndex);

    const { trackFormSections } = useBundleMixpanelHook();
    const { trackBbUpsellButton } = useBundleBuilderMixpanelHook();
    const { control, formState, setValue } = useFormContext();
    const showProductPriceWithComparePrice = useWatch({ control, name: "show_product_price_with_compare_price" });
    const limitShowOfferCta = useWatch({ control, name: "limit_show_offer_cta" });
    const showOfferCtaPages = useWatch({ control, name: "show_offer_cta_pages" }) ?? [];
    const bundleBuilderLink = useWatch({ control, name: "bundle_builder_link" }) ?? "";
    const showSearchBar = useWatch({ control, name: "show_search_bar" });
    const searchBarAlignmentDesktop = useWatch({ control, name: "search_bar_alignment_desktop" });
    const searchBarAlignmentMobile = useWatch({ control, name: "search_bar_alignment_mobile" });
    const applyDiscountOnComparePrice = useWatch({ control, name: "apply_discount_on_compare_price" });
    const selectedTemplateBb = useWatch({ control, name: "selected_template_bb" });
    const isPricingHiddenTemplate = [
        AvailableBundleBuilderTemplates.BASIC_CARD_INFO,
        AvailableBundleBuilderTemplates.IMG_FOCUS_CARD,
        AvailableBundleBuilderTemplates.HOVER_INFO_CARD,
    ].includes(selectedTemplateBb);
    const limitMaxQuantityPerProduct = useWatch({ control, name: "limit_max_quantity_per_product" });
    const allowPreSelectedProducts = useWatch({ control, name: "allow_pre_selected_products" });
    const preSelectedProducts = useWatch({ control, name: "pre_selected_products" }) ?? [];
    const bundleEmbedId = useWatch({ control, name: "bundle_embed_id" });
    const allowConditionalLogic = useWatch({ control, name: "allow_conditional_logic" });
    const productConditions = useWatch({ control, name: "product_conditions" }) ?? [];
    const redirectOptions = useWatch({ control, name: "redirect_options" });
    const showPopupOnCartRedirect = useWatch({ control, name: "show_popup_on_cart_redirect" });
    const popupText = useWatch({ control, name: "popup_text" });
    const allowExcludeFromStepConditions = useWatch({ control, name: "allow_exclude_from_step_conditions" });
    const steps = (useWatch({ control, name: "steps" }) ?? []) as StepsType[];
    const formId = useWatch({ control, name: "form_id" });
    const showProductGridView = useWatch({ control, name: "show_product_grid_view" });

    const appConfigContext = useContext(AppConfigContext);

    const handleProductSearch = async (initialQuery: string = "") => {
        const preSelected = showOfferCtaPages?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: VariantsObj) => ({ id: variant?.id })),
        }));
        const selected: any = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: initialQuery,
            action: "select",
            selectionIds: preSelected,
            filter: { query: "status:active AND published_status:published", variants: false },
        });
        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.flatMap((product: any) => {
                productsArr.push({
                    id: product?.id,
                    images: product?.images,
                    title: product?.title,
                    handle: product?.handle,
                    quantity: 1,
                    maxProductStock: product?.totalInventory,
                    selectedVariants: [],
                    subscriptions: [],
                    oneTimePurchase: true,
                    isSubscribed: false,
                    variants: product?.variants?.map((variant: VariantsObj) => ({
                        image: { ...variant.image },
                        id: variant?.id,
                        displayName: variant.displayName,
                        title: variant?.title?.toLowerCase() == "default title" ? product?.title : variant?.title,
                        price: variant?.price,
                        quantity: 1,
                        selectedOptions: variant?.selectedOptions,
                        copyPrice: variant.price,
                        compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                    })),
                    options: product?.options,
                });
            });
            setValue("show_offer_cta_pages", productsArr, { shouldDirty: true });
        }
    };

    const removeProduct = (pId: string = "") => {
        const temp: ProductObj[] = [...showOfferCtaPages];
        const idx = temp.findIndex((p) => p.id == pId);
        if (idx >= 0) {
            temp.splice(idx, 1);
            setValue("show_offer_cta_pages", temp, { shouldDirty: true });
        }
    };

    const handleCollectionSearch = async (initialQuery: string = "") => {
        const preSelected = showOfferCtaPages?.map((collection: any) => ({ id: collection.id }));
        const selected = await shopify.resourcePicker({
            type: "collection",
            multiple: true,
            query: initialQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                // query: "status:active",
                // variants: false,
            },
        });

        const collectionsArr: any = [];

        if (selected) {
            selected?.map((coll: any) => {
                collectionsArr.push({ id: coll?.id, handle: coll?.handle, image: coll.image, title: coll.title });
            });
            setValue("show_offer_cta_pages", collectionsArr, { shouldDirty: true });
        }
    };

    const removeCollection = (pId: string = "") => {
        const temp = [...showOfferCtaPages];
        const idx = temp.findIndex((p: any) => p.id == pId);
        if (idx >= 0) {
            temp.splice(idx, 1);
            setValue("show_offer_cta_pages", temp, { shouldDirty: true });
        }
    };

    const updateVisibility = (val: string) => {
        setValue("show_offer_cta_pages", [], { shouldDirty: true });
        trackBbUpsellButton({ cta: "Upsell Button Visibility", ctaType: val });
    };

    const fullUrl = bundleBuilderLink || "";

    const [baseUrl, initialSlug] = useMemo(() => {
        const splitIndex = fullUrl.indexOf("/pages/");
        if (splitIndex === -1) return [fullUrl, ""];
        return [
            fullUrl.substring(0, splitIndex + 7), // include "/pages/"
            fullUrl.substring(splitIndex + 7), // slug part
        ];
    }, [fullUrl]);

    const [slugValue, setSlugValue] = useState(initialSlug);

    useEffect(() => {
        setSlugValue(initialSlug);
    }, [initialSlug]);

    const handleSlugChange = (value: string) => {
        const sanitized = value.toLowerCase().replace(/[^a-z0-9-.]/g, "");

        setSlugValue(sanitized);
    };

    const handleSlugSave = () => {
        const newUrl = `${baseUrl}${slugValue}`;
        setValue("bundle_builder_link", newUrl, { shouldDirty: true });
    };

    const handleSlugDiscard = () => {
        setSlugValue(initialSlug);
    };

    const finalUrl = `${baseUrl}${slugValue}`;
    const bundleBuilderLinkError = (formState.errors as any)?.bundle_builder_link?.message as string | undefined;
    const popupRedirectUrlError = (formState.errors as any)?.popup_redirect_url?.message as string | undefined;
    const isBundleBuilderLinkDisabled = useVariantFieldDisabled("bundle_builder_link");

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandBundleVisibility((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <MantleDoc handle={mantleDocHandles[bundleType].bundleVisibility} title="Bundle Visibility" />

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandBundleVisibility ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandBundleVisibility}
                id="basic-collapsible"
                transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                expandOnPrint
            >
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row2}`} style={{ marginTop: -3 }}>
                        <span style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                            <Label id="widget_icon">Add upsell button on</Label>
                            <Tooltip content="Boost the visibility of your bundle by adding a call-to-action (CTA) button on the product page. This button directs shoppers to your bundle builder page.">
                                <Icon source={InfoIcon} />
                            </Tooltip>
                        </span>
                        <div style={{ display: "flex", flexDirection: "column", marginTop: "-18px" }}>
                            <FormRadioButton
                                label="All Product Pages"
                                id="app"
                                key="app"
                                name="limit_show_offer_cta"
                                value="all"
                                onChangeHook={() => updateVisibility("all")}
                            />
                            <FormRadioButton
                                label="Specific Product Pages"
                                id="spp"
                                key="spp"
                                name="limit_show_offer_cta"
                                value="products"
                                onChangeHook={() => updateVisibility("products")}
                            />
                            {limitShowOfferCta == "products" && (
                                <div style={{ marginLeft: 25 }}>
                                    {showOfferCtaPages?.length ? (
                                        <RenderPages
                                            show_offer_cta_pages={{ value: showOfferCtaPages }}
                                            removeProduct={removeProduct}
                                            type="products"
                                        />
                                    ) : (
                                        <div
                                            className={styles.emptyProductBox}
                                            style={{ marginBottom: 10 }}
                                            onClick={() => handleProductSearch()}
                                        >
                                            <Button variant="secondary">+ Add Products</Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            <FormRadioButton
                                label="Products in Specific Collection"
                                id="spc"
                                key="spc"
                                name="limit_show_offer_cta"
                                value="collections"
                                onChangeHook={() => updateVisibility("collections")}
                            />
                            {limitShowOfferCta == "collections" && (
                                <div style={{ marginLeft: 25 }}>
                                    {showOfferCtaPages?.length ? (
                                        <RenderPages
                                            show_offer_cta_pages={{ value: showOfferCtaPages }}
                                            removeProduct={removeCollection}
                                            type="collections"
                                        />
                                    ) : (
                                        <div
                                            className={styles.emptyProductBox}
                                            style={{ marginBottom: 10 }}
                                            onClick={() => handleCollectionSearch()}
                                        >
                                            <Button variant="secondary">+ Add Collections</Button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <FormRadioButton
                                label="No Product Pages"
                                id="nop"
                                key="nop"
                                name="limit_show_offer_cta"
                                value="none"
                                onChangeHook={() => updateVisibility("none")}
                            />
                        </div>

                        <Label id="Upsell button position">
                            <div style={{ display: "flex", alignItems: "center", color: "#303030", marginBottom: -10 }}>
                                Upsell button position
                            </div>
                        </Label>
                        <div style={{ lineHeight: "20px", display: "flex", flexDirection: "column", gap: 10 }}>
                            <div>
                                By default, the widget will appear <b>{DefaultWidgetPositions[bundleType]}</b>. <br />{" "}
                                To change its position, open the &nbsp;
                                <Link
                                    url={getAppBlockDeepLink({
                                        shop: appConfigContext.shop,
                                        extensionId: appConfigContext.extensionId,
                                        bundleType,
                                    })}
                                    external
                                >
                                    Shopify Theme Editor
                                </Link>
                                &nbsp;,and drag the app block to your preferred location.
                            </div>
                            <div>
                                <Link
                                    external
                                    url="https://bundles-help.keficommerce.com/faq/how-to-add-the-kefi-bundle-builder-on-any-page-in-your-shopify-theme"
                                >
                                    Need help?
                                </Link>
                            </div>
                        </div>
                        <Divider />
                        <FormTextField
                            name="offer_cta_text"
                            label="Upsell Button Text"
                            placeholder="Upsell Button Text"
                            maxLength={80}
                            showCharacterCount
                        />

                        <Divider />

                        <BlockStack gap="200">
                            <BlockStack gap="100">
                                <InlineStack gap="100" align="start">
                                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                                        Bundle Builder Link
                                    </Text>
                                    <Tooltip
                                        width="default"
                                        content="This is the link to the bundle builder page, you can use it to link this page to any custom CTA on your store or for marketing purpose. Only the slug (after /pages/) can be edited."
                                    >
                                        <Icon source={InfoIcon} tone="subdued" />
                                    </Tooltip>
                                </InlineStack>
                                <Text as="p" variant="bodyMd" tone="subdued">
                                    Click the slug to customise your page URL
                                </Text>
                            </BlockStack>

                            <FormTextField
                                name="bundle_builder_link"
                                label=""
                                labelHidden
                                disabled={!formId || !finalUrl || isBundleBuilderLinkDisabled}
                                error={bundleBuilderLinkError}
                                prefix={
                                    <span
                                        style={{
                                            color: "#6d7175",
                                            fontSize: "13px",
                                            userSelect: "none",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        pages/
                                    </span>
                                }
                                suffix={<Icon source={EditIcon} />}
                                value={slugValue}
                                onChange={handleSlugChange}
                                connectedRight={
                                    <div style={{ marginLeft: "8px" }}>
                                        <Button
                                            onClick={() => handleCopyText(finalUrl, true)}
                                            size="slim"
                                            icon={<Icon source={ClipboardIcon} />}
                                            disabled={!slugValue?.length}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                }
                            />

                            {slugValue !== initialSlug && !isBundleBuilderLinkDisabled && (
                                <InlineStack gap="200" align="end">
                                    <Button onClick={handleSlugDiscard}>Discard</Button>
                                    <Button variant="primary" onClick={handleSlugSave}>
                                        Save
                                    </Button>
                                </InlineStack>
                            )}
                        </BlockStack>

                        {!!bundleEmbedId && (
                            <BundleEmbed bundleType={bundleType} bundle_embed_id={{ value: bundleEmbedId }} />
                        )}
                    </div>
                </FormLayout>
            </Collapsible>
            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandProductSettings((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <div>Product Settings</div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandProductSettings ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandProductSettings}
                id="basic-collapsible"
                transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                expandOnPrint
            >
                <Box>
                    <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
                        <Box padding="400" paddingBlockStart="0">
                            <Card>
                                {selectedTab === 0 && (
                                    <BlockStack>
                                        <FormSettingRow
                                            name="hide_out_of_stock_products"
                                            label="Hide Out of Stock Products"
                                            description="Automatically hide products that are out of stock."
                                        />
                                        <FormSettingRow
                                            name="disable_cta_when_stock_reached"
                                            label="Disable quantity increase when stock limit is reached"
                                            description="Disable the + quantity button once the available inventory or maximum stock limit is reached."
                                            tooltipContent={
                                                <Text as="p" variant="bodyMd">
                                                    When enabled, customers cannot increase the quantity beyond the
                                                    available inventory. This setting overrides Shopify's{" "}
                                                    <Text as="span" variant="bodyMd" fontWeight="bold">
                                                        Continue selling when out of stock{" "}
                                                    </Text>
                                                    and{" "}
                                                    <Text as="span" variant="bodyMd" fontWeight="bold">
                                                        Inventory not tracked{" "}
                                                    </Text>
                                                    settings. The + quantity button is disabled once the available
                                                    inventory or stock limit is reached.
                                                </Text>
                                            }
                                        />
                                        <FormSettingRow
                                            name="show_search_bar"
                                            hideDivider
                                            label="Show product search"
                                            description="Allow searching products when building or configuring bundles."
                                        />
                                        {showSearchBar && (
                                            <div style={{ paddingLeft: "15px", paddingBottom: "15px" }}>
                                                <BlockStack gap="200">
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                                                            Desktop Alignment
                                                        </Text>
                                                        <InlineStack gap="200">
                                                            {[
                                                                {
                                                                    value: "flex-start",
                                                                    icon: <AiOutlineAlignLeft size={20} />,
                                                                },
                                                                {
                                                                    value: "center",
                                                                    icon: <AiOutlineAlignCenter size={20} />,
                                                                },
                                                                {
                                                                    value: "flex-end",
                                                                    icon: <AiOutlineAlignRight size={20} />,
                                                                },
                                                            ].map((opt) => (
                                                                <div
                                                                    key={opt.value}
                                                                    title={opt.value}
                                                                    onClick={() =>
                                                                        setValue(
                                                                            "search_bar_alignment_desktop",
                                                                            opt.value,
                                                                            { shouldDirty: true },
                                                                        )
                                                                    }
                                                                    className={`${styles.boxIcon} ${searchBarAlignmentDesktop === opt.value ? styles.selectedIcon : ""}`}
                                                                    style={{ cursor: "pointer" }}
                                                                >
                                                                    {opt.icon}
                                                                </div>
                                                            ))}
                                                        </InlineStack>
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                                                            Mobile Alignment
                                                        </Text>
                                                        <InlineStack gap="200">
                                                            {[
                                                                {
                                                                    value: "flex-start",
                                                                    icon: <AiOutlineAlignLeft size={20} />,
                                                                },
                                                                {
                                                                    value: "center",
                                                                    icon: <AiOutlineAlignCenter size={20} />,
                                                                },
                                                                {
                                                                    value: "flex-end",
                                                                    icon: <AiOutlineAlignRight size={20} />,
                                                                },
                                                            ].map((opt) => (
                                                                <div
                                                                    key={opt.value}
                                                                    title={opt.value}
                                                                    onClick={() =>
                                                                        setValue(
                                                                            "search_bar_alignment_mobile",
                                                                            opt.value,
                                                                            { shouldDirty: true },
                                                                        )
                                                                    }
                                                                    className={`${styles.boxIcon} ${searchBarAlignmentMobile === opt.value ? styles.selectedIcon : ""}`}
                                                                    style={{ cursor: "pointer" }}
                                                                >
                                                                    {opt.icon}
                                                                </div>
                                                            ))}
                                                        </InlineStack>
                                                    </div>
                                                </BlockStack>
                                            </div>
                                        )}
                                        <Divider />
                                        <FormSettingRow
                                            name="hide_bundle_header"
                                            label="Hide bundle header"
                                            description="Hide the title and header displayed above the bundle widget."
                                        />
                                        <FormSettingRow
                                            name="hide_product_info_modal"
                                            label="Hide product info modal"
                                            description="Disable the product details modal shown on product click."
                                        />
                                        <FormSettingRow
                                            name="disable_product_title_link"
                                            label="Disable redirection to product page"
                                            description="Prevent customers from being redirected when clicking a product inside the bundle."
                                        />
                                        <FormSettingRow
                                            name="bundles_design.is_product_img_height_auto"
                                            label="Product Image Auto Height"
                                            description="If enabled, product image height will be auto and max height will be 350px for desktop and 250px for mobile."
                                        />
                                        {isDispensaryCoStore && (
                                            <>
                                                <FormSettingRow
                                                    name="show_total_quantity_in_footer"
                                                    label="Show total quantity in footer"
                                                    description="Show total quantity of selected products in footer."
                                                />
                                            </>
                                        )}
                                        <Box paddingBlock="400">
                                            <BlockStack gap="200">
                                                <InlineGrid columns={{ xs: "1fr auto" }} alignItems="center" gap="400">
                                                    <BlockStack gap="100">
                                                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                                                            Substep Layout
                                                        </Text>
                                                        <Text as="p" variant="bodyMd" tone="subdued">
                                                            Choose how categories/substeps are displayed: Tabs
                                                            (horizontal tab bar) or Sequential (all substeps stacked
                                                            vertically).
                                                        </Text>
                                                    </BlockStack>
                                                    <div style={{ minWidth: "150px" }}>
                                                        <FormSelect
                                                            name="substep_layout"
                                                            label=""
                                                            labelHidden
                                                            options={[
                                                                { label: "Tabs", value: "tabs" },
                                                                { label: "Sequential", value: "sequential" },
                                                            ]}
                                                        />
                                                    </div>
                                                </InlineGrid>
                                            </BlockStack>
                                        </Box>
                                        <Divider />
                                        <FormSettingRow
                                            name="show_product_grid_view"
                                            label="Show product grid instead of manual product bars"
                                            description="Display selected products in the review area as a compact card grid instead of the default horizontal product bars."
                                            onChangeHook={(val: any) => {
                                                if (!val) {
                                                    setValue("show_broken_quantity_in_grid", false);
                                                }
                                            }}
                                            hideDivider={true}
                                        />
                                        {showProductGridView && (
                                            <Box>
                                                <Divider />
                                                <FormSettingRow
                                                    name="show_broken_quantity_in_grid"
                                                    label="Show broken quantity in grid"
                                                    description="When a product is added multiple times, show one card per unit instead of a single card with a quantity badge."
                                                    hideDivider={true}
                                                />
                                            </Box>
                                        )}
                                    </BlockStack>
                                )}

                                {selectedTab === 1 && (
                                    <BlockStack>
                                        {isPricingHiddenTemplate && (
                                            <LegacyCard title="">
                                                <Banner tone={"info"}>
                                                    <p>
                                                        The selected template doesn't show prices on product cards, so
                                                        pricing options are disabled.
                                                    </p>
                                                </Banner>
                                            </LegacyCard>
                                        )}
                                        <FormSettingRow
                                            name="use_compare_at_price"
                                            label="Display ‘Compare at’ price"
                                            description="Show the Compare-at price in the product card."
                                            disabled={
                                                isPricingHiddenTemplate ||
                                                applyDiscountOnComparePrice ||
                                                showProductPriceWithComparePrice
                                            }
                                        />
                                        <FormSettingRow
                                            name="apply_discount_on_compare_price"
                                            label="Apply discount on the Compare-at Price"
                                            description="Calculate discount based on Compare-at Price instead of selling price."
                                            tooltipContent={
                                                <BlockStack gap="200">
                                                    <Text as="p">
                                                        Your discount will be calculated based on the product’s
                                                        Compare-at Price instead of the actual selling price.
                                                    </Text>
                                                    <Text as="p" variant="bodyMd" fontWeight="bold">
                                                        Important Note
                                                    </Text>
                                                    <Text as="p">
                                                        Your discount must be{" "}
                                                        <Text as="span" fontWeight="semibold">
                                                            greater than the discount Shopify already shows &nbsp;
                                                        </Text>
                                                        between the Compare-at Price and the Actual Price.
                                                    </Text>
                                                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                                                        Example:
                                                    </Text>
                                                    <List type="bullet">
                                                        <List.Item>Compare-at Price: $100</List.Item>
                                                        <List.Item>Actual Price: $90</List.Item>
                                                        <List.Item>
                                                            → Shopify is already showing a 10% discount.
                                                        </List.Item>
                                                    </List>
                                                    <Text as="p" variant="bodyMd">
                                                        To apply your custom discount correctly, you must set a discount
                                                        <Text as="span" fontWeight="semibold">
                                                            &nbsp;greater than 10% in app
                                                        </Text>
                                                    </Text>
                                                </BlockStack>
                                            }
                                            onChangeHook={(val) => {
                                                if (val) {
                                                    setValue("use_compare_at_price", val, { shouldDirty: true });
                                                    setValue("show_product_price_with_compare_price", false, {
                                                        shouldDirty: true,
                                                    });
                                                }
                                            }}
                                            disabled={isPricingHiddenTemplate}
                                        />
                                        <FormSettingRow
                                            name="show_product_price_with_compare_price"
                                            hideDivider
                                            label="Display ‘Compare at’ price & Product Price"
                                            description="Show the Compare-at price & Product Price in the product card."
                                            onChangeHook={(val) => {
                                                setValue("use_compare_at_price", val, { shouldDirty: true });
                                            }}
                                            disabled={isPricingHiddenTemplate || applyDiscountOnComparePrice}
                                        />
                                    </BlockStack>
                                )}

                                {selectedTab === 2 && (
                                    <BlockStack>
                                        <FormSettingRow
                                            name="limit_max_quantity_per_product"
                                            hideDivider
                                            label="Max Quantity Selection Per Product"
                                            description="Sets the maximum number of units a customer can add for each product."
                                        />
                                        {limitMaxQuantityPerProduct && (
                                            <Box paddingBlockStart="200">
                                                <FormTextField
                                                    name="max_quantity_per_product"
                                                    label="Max Quantity Selection Per Product"
                                                    labelHidden
                                                    disabled={!limitMaxQuantityPerProduct}
                                                    placeholder=""
                                                    type="number"
                                                    min={0}
                                                    max={20}
                                                    onChange={(val) => {
                                                        let numVal = +val;
                                                        if (numVal > 20) numVal = 20;
                                                        setValue("max_quantity_per_product", numVal, {
                                                            shouldDirty: true,
                                                        });
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Divider />
                                        <FormSettingRow
                                            name="allow_pre_selected_products"
                                            hideDivider
                                            label="Pre-select items by default"
                                            description="Automatically selects these items when the bundle loads."
                                            onChangeHook={(val) => {
                                                if (val === false) {
                                                    setValue("pre_selected_products", [], { shouldDirty: true });
                                                }
                                            }}
                                        />
                                        {allowPreSelectedProducts && (
                                            <Box paddingBlockStart="200">
                                                <PreSelectedProducts
                                                    preSelectedProducts={preSelectedProducts}
                                                    setValue={setValue}
                                                    sft={appConfigContext.sft}
                                                    steps={steps}
                                                />
                                            </Box>
                                        )}
                                        <Divider />
                                        <FormSettingRow
                                            name="allow_quantity_input"
                                            hideDivider
                                            label="Allow quantity input"
                                            description="Allow customers to select the quantity of each product."
                                        />
                                        {isLilSweetTreatStore && (
                                            <>
                                                <Divider />
                                                <FormSettingRow
                                                    name="allow_exclude_from_step_conditions"
                                                    hideDivider
                                                    label="Exclude products from step conditions"
                                                    description="Specify products that should not be counted toward step-level quantity limits."
                                                />
                                                {allowExcludeFromStepConditions && (
                                                    <ExemptProductsManager
                                                        steps={steps}
                                                        sft={appConfigContext.sft}
                                                        setValue={setValue}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </BlockStack>
                                )}

                                {selectedTab === 3 && (
                                    <BlockStack>
                                        <FormSettingRow
                                            name="allow_conditional_logic"
                                            hideDivider
                                            label="Enable conditional rules"
                                            description="Show or hide items based on rules (e.g., ‘If product A selected → show product B’)."
                                            onChangeHook={(val) => {
                                                if (val && productConditions.length === 0) {
                                                    const emptyCondition = [
                                                        {
                                                            included_products: [],
                                                            hidden_products: [],
                                                            hidden_steps: [],
                                                            hidden_substeps: [],
                                                            action_type: "products" as const,
                                                        },
                                                    ];
                                                    setValue("product_conditions", emptyCondition, {
                                                        shouldDirty: true,
                                                    });
                                                }
                                            }}
                                        />
                                        {allowConditionalLogic && (
                                            <Box paddingBlockStart="200">
                                                <ConditionalProducts
                                                    productConditions={productConditions}
                                                    setValue={setValue}
                                                    steps={steps}
                                                />
                                            </Box>
                                        )}
                                        <Divider />
                                        <FormSettingRow
                                            name="auto_advance_when_condition_matched"
                                            hideDivider
                                            label="Move to Next Step Automatically"
                                            description="Automatically move to the next step when conditions are met."
                                        />
                                    </BlockStack>
                                )}
                            </Card>
                        </Box>
                    </Tabs>
                </Box>
            </Collapsible>
            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandCheckoutSettings((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <div>Checkout Settings</div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandCheckoutSettings ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>

            <Collapsible
                open={expandCheckoutSettings}
                id="basic-collapsible"
                transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                expandOnPrint
            >
                <FormLayout>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <FormRadioButton
                            label="Redirect to Checkout"
                            helpText=""
                            name="redirect_options"
                            value="checkout"
                            onChangeHook={() => setValue("checkout_cta_text", "Go to Checkout", { shouldDirty: true })}
                        />
                        <FormRadioButton
                            label="Redirect to Cart"
                            helpText=""
                            name="redirect_options"
                            value="cart"
                            onChangeHook={() => setValue("checkout_cta_text", "Add to Cart", { shouldDirty: true })}
                        />

                        {redirectOptions == "cart" && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    padding: "10px",
                                    borderRadius: "8px",
                                    backgroundColor: "#f9f9f9",
                                }}
                            >
                                <div className={styles.subStepTogglerWrapper}>
                                    <FormToggle name="show_popup_on_cart_redirect" zoom={0.8} />{" "}
                                    <Label id="hello">Show Popup on Add to Cart Click</Label>
                                </div>

                                {showPopupOnCartRedirect && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                        <TextEditorCart
                                            placeholder="Yay! Your bundle has been added to cart!"
                                            value={popupText}
                                            onChange={(value: any, _: any, source: string) => {
                                                if (source == "user") {
                                                    const cleanedValue = value === "<p><br></p>" ? "" : value;
                                                    setValue("popup_text", cleanedValue, { shouldDirty: true });
                                                }
                                            }}
                                            title="Popup Text"
                                            _style={{ zIndex: 10 }}
                                        />
                                        <div style={{ zIndex: 0 }}>
                                            <FormTextField
                                                name="popup_redirect_url"
                                                label="Redirect URL for Continue Shopping CTA"
                                                placeholder="https://example.com"
                                                error={popupRedirectUrlError}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </FormLayout>
            </Collapsible>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>
        </>
    );
};

export default BundleBuilderAdvanced;

const PreSelectedProducts = ({
    preSelectedProducts,
    setValue,
    sft,
    steps,
}: {
    preSelectedProducts: any[];
    setValue: any;
    sft: string;
    steps: StepsType[];
}) => {
    const [productSearchQuery, setProductSearchQuery] = useState("");

    const handleProductSearch = async () => {
        const preSelected = preSelectedProducts?.map((product: ProductObj) => ({ id: product?.id }));
        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: productSearchQuery,
            action: "select",
            selectionIds: preSelected,
            filter: { query: "status:active AND published_status:published", variants: false },
        });
        if (selected) {
            const productsArr: any = [];
            selected?.map((product: any) => {
                const existingProduct = preSelectedProducts?.find((p: any) => p.id === product.id);

                productsArr.push({
                    id: product?.id,
                    title: product?.title,
                    quantity: existingProduct?.quantity || 1,
                    image: product?.images?.[0]?.originalSrc,
                    variants: product.variants.map((v: any) => ({ id: v.id })),
                });
            });
            setProductSearchQuery("");

            try {
                const newProductsArr = await getProductsDynamicallyAndReturnNewProductsArray(productsArr, sft);
                setValue("pre_selected_products", newProductsArr, { shouldDirty: true });
            } catch (error) {
                console._log("Couldn't fetch products:", error);
                setValue("pre_selected_products", productsArr, { shouldDirty: true });
            }
        }
    };

    const removeProducts = (pId: string = "") => {
        const temp = [...preSelectedProducts];
        const idx = temp.findIndex((p: any) => p.id == pId);

        if (idx >= 0) {
            temp.splice(idx, 1);
            setValue("pre_selected_products", temp, { shouldDirty: true });
        }
    };

    useEffect(() => {
        if (!preSelectedProducts?.length) return;

        const stepProducts = steps.flatMap((s) =>
            s?.enable_subSteps ? s.subSteps.flatMap((ss) => ss.products.map((p) => p.id)) : s.products.map((p) => p.id),
        );

        const stepCollections = steps.flatMap((s) =>
            s?.enable_subSteps
                ? s.subSteps.flatMap((ss) => ss.collections.map((c) => c.id))
                : s.collections.map((c) => c.id),
        );

        const newPreSelectedProductsList = preSelectedProducts.filter((product) => {
            const isDirectProduct = stepProducts.includes(product.id);
            // @ts-expect-error hack
            const isInAllowedCollection = product?.collections?.some((c) => stepCollections.includes(c));
            return isDirectProduct || isInAllowedCollection;
        });

        if (newPreSelectedProductsList.length !== preSelectedProducts?.length) {
            setValue("pre_selected_products", newPreSelectedProductsList, { shouldDirty: true });
            shopify.toast.show("Only products included in the bundle can be set as preselected", {
                isError: true,
                duration: 1500,
            });
        }
    }, [preSelectedProducts]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                padding: "10px",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
            }}
        >
            <h2>Add Pre-selected Products</h2>
            <TextField
                label="Search Product"
                labelHidden
                placeholder="Search Products"
                autoComplete="off"
                value={productSearchQuery}
                onChange={(val) => setProductSearchQuery(val)}
                prefix={<Icon source={SearchIcon} />}
                size="slim"
                connectedRight={
                    <div style={{ marginLeft: "8px" }}>
                        <Button onClick={() => handleProductSearch()} size="slim">
                            Browse
                        </Button>
                    </div>
                }
            />

            {preSelectedProducts?.length > 0 && (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                        border: "1px solid rgba(227,227,227,1)",
                        borderRadius: "12px",
                        backgroundColor: "#ffffff",
                        padding: "10px",
                    }}
                >
                    {preSelectedProducts?.map((p: any, index: number) => (
                        <div
                            key={index}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "10px",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    gap: "10px",
                                    flexGrow: "1",
                                    maxWidth: "68%",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        overflow: "hidden",
                                    }}
                                >
                                    <Thumbnail source={p?.image || CollectionIcon} alt={p?.title} size="small" />
                                </div>
                                <p>{p?.title}</p>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    minWidth: "120px",
                                    maxWidth: "120px",
                                }}
                            >
                                <Label id="">Qty</Label>
                                <FormTextField
                                    name={`pre_selected_products.${index}.quantity`}
                                    type="number"
                                    value={p?.quantity?.toString() || "1"}
                                    min={1}
                                    onChange={(value) => {
                                        const newQty = parseInt(value) || 1;
                                        const updatedProducts = [...preSelectedProducts];
                                        updatedProducts[index] = {
                                            ...updatedProducts[index],
                                            quantity: Math.max(1, newQty),
                                        };
                                        setValue("pre_selected_products", updatedProducts, { shouldDirty: true });
                                    }}
                                    labelHidden
                                    label="Qty"
                                />
                            </div>

                            <div onClick={() => removeProducts(p.id)} style={{ cursor: "pointer" }}>
                                <CloseIcon />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <FormCheckbox name="make_pre_selected_compulsory" label="Make pre-selected products compulsary" />
        </div>
    );
};

const ConditionalProducts = ({
    productConditions,
    setValue,
    steps,
}: {
    productConditions: any[];
    setValue: any;
    steps: any[];
}) => {
    const [showProductModal, setShowProductModal] = useState(false);
    const [showStepModal, setShowStepModal] = useState(false);
    const [activeConditionIndex, setActiveConditionIndex] = useState<number | null>(null);
    const [activeProductType, setActiveProductType] = useState<"included_products" | "hidden_products" | null>(null);

    const handleProductSearch = async (conditionIdx: number, type: "included_products" | "hidden_products") => {
        const currentList = productConditions[conditionIdx][type];

        const preSelected = currentList?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: any) => ({ id: variant?.id })),
        }));

        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: "",
            action: "select",
            selectionIds: preSelected,
            filter: { query: "status:active AND published_status:published", variants: true },
        });

        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.flatMap((product: any) => {
                productsArr.push({
                    id: product?.id,
                    images: product?.images,
                    title: product?.title,
                    handle: product?.handle,
                    quantity: 1,
                    maxProductStock: product?.totalInventory,
                    selectedVariants: [],
                    subscriptions: [],
                    oneTimePurchase: true,
                    isSubscribed: false,
                    variants: product?.variants?.map((variant: VariantsObj) => ({
                        image: { ...variant.image },
                        id: variant?.id,
                        displayName: variant.displayName,
                        title: variant?.title?.toLowerCase() == "default title" ? product?.title : variant?.title,
                        price: variant?.price,
                        quantity: 1,
                        selectedOptions: variant?.selectedOptions,
                        copyPrice: variant.price,
                        compareAtPrice: variant?.compareAtPrice ? variant.compareAtPrice : variant.price,
                    })),
                    options: product?.options,
                });
            });

            const updatedConditions = [...productConditions];
            updatedConditions[conditionIdx] = { ...updatedConditions[conditionIdx], [type]: productsArr };
            setValue("product_conditions", updatedConditions, { shouldDirty: true });
        }
    };

    const openProductModal = (index: number, type: "included_products" | "hidden_products") => {
        setActiveConditionIndex(index);
        setActiveProductType(type);
        setShowProductModal(true);
    };

    const openStepModal = (index: number) => {
        setActiveConditionIndex(index);
        setShowStepModal(true);
    };

    const handleStepToggle = (stepLabel: string, subStepLabel?: string) => {
        if (activeConditionIndex === null) return;

        const updatedConditions = [...productConditions];
        if (subStepLabel) {
            const currentSubSteps = updatedConditions[activeConditionIndex].hidden_substeps || [];
            const key = `${stepLabel}:${subStepLabel}`;
            const newSubSteps = currentSubSteps.includes(key)
                ? currentSubSteps.filter((s: string) => s !== key)
                : [...currentSubSteps, key];
            updatedConditions[activeConditionIndex] = {
                ...updatedConditions[activeConditionIndex],
                hidden_substeps: newSubSteps,
            };
        } else {
            const currentSteps = updatedConditions[activeConditionIndex].hidden_steps || [];
            const newSteps = currentSteps.includes(stepLabel)
                ? currentSteps.filter((s: string) => s !== stepLabel)
                : [...currentSteps, stepLabel];
            updatedConditions[activeConditionIndex] = {
                ...updatedConditions[activeConditionIndex],
                hidden_steps: newSteps,
            };
        }

        setValue("product_conditions", updatedConditions, { shouldDirty: true });
    };

    const handleRemoveConditionStep = (conditionIdx: number) => {
        const updatedConditions = [...productConditions];
        updatedConditions.splice(conditionIdx, 1);
        setValue("product_conditions", updatedConditions, { shouldDirty: true });
    };

    const handleProductDelete = (productId: string) => {
        if (activeConditionIndex === null || !activeProductType) return;

        const updatedConditions = [...productConditions];
        const productList = updatedConditions[activeConditionIndex][activeProductType];
        const tmp = productList.filter((p: ProductObj) => p.id !== productId);
        updatedConditions[activeConditionIndex] = {
            ...updatedConditions[activeConditionIndex],
            [activeProductType]: tmp,
        };
        setValue("product_conditions", updatedConditions, { shouldDirty: true });
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                padding: "10px",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
            }}
        >
            <h2>Conditional Products</h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {productConditions?.map(
                    (
                        _p: {
                            included_products: ProductObj[];
                            hidden_products: ProductObj[];
                            hidden_steps: string[];
                            hidden_substeps: string[];
                            action_type: "products" | "steps";
                        },
                        index: number,
                    ) => (
                        <div
                            key={index}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                border: "1px solid #ccc",
                                padding: "14px 28px 14px 14px",
                                borderRadius: "6px",
                                position: "relative",
                            }}
                        >
                            <span style={{ position: "absolute", top: "2px", right: "2px" }}>
                                <Button
                                    onClick={() => handleRemoveConditionStep(index)}
                                    icon={DeleteIcon}
                                    variant="plain"
                                    tone="critical"
                                    accessibilityLabel="Delete"
                                    size="slim"
                                ></Button>
                            </span>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "5px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <p>If Selected Products Include</p>
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <Button onClick={() => handleProductSearch(index, "included_products")}>
                                        Select Products
                                    </Button>
                                    <p
                                        style={{
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            color: "#007ace",
                                            minWidth: "80px",
                                        }}
                                        onClick={() => openProductModal(index, "included_products")}
                                    >
                                        {_p.included_products.length} selected
                                    </p>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "10px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <div style={{ minWidth: "150px" }}>
                                    <FormSelect
                                        name={`product_conditions.${index}.action_type`}
                                        label=""
                                        labelHidden
                                        options={[
                                            { label: "Hidden Products", value: "products" },
                                            { label: "Hidden Steps", value: "steps" },
                                        ]}
                                        value={_p.action_type || "products"}
                                        onChange={(val: string) => {
                                            const updatedConditions = [...productConditions];
                                            updatedConditions[index] = {
                                                ...updatedConditions[index],
                                                action_type: val as "products" | "steps",
                                            };
                                            setValue("product_conditions", updatedConditions, {
                                                shouldDirty: true,
                                            });
                                        }}
                                    />
                                </div>
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    {_p.action_type === "products" || !_p.action_type ? (
                                        <>
                                            <Button onClick={() => handleProductSearch(index, "hidden_products")}>
                                                Select Products
                                            </Button>
                                            <p
                                                style={{
                                                    textDecoration: "underline",
                                                    cursor: "pointer",
                                                    color: "#007ace",
                                                    minWidth: "80px",
                                                }}
                                                onClick={() => openProductModal(index, "hidden_products")}
                                            >
                                                {_p.hidden_products?.length || 0} selected
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <Button onClick={() => openStepModal(index)}>Select Steps</Button>
                                            <p
                                                style={{
                                                    textDecoration: "underline",
                                                    cursor: "pointer",
                                                    color: "#007ace",
                                                    minWidth: "80px",
                                                }}
                                                onClick={() => openStepModal(index)}
                                            >
                                                {(_p.hidden_steps?.length || 0) + (_p.hidden_substeps?.length || 0)}
                                                selected
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ),
                )}

                <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => {
                        const updatedConditions = [...productConditions];
                        updatedConditions.push({
                            included_products: [],
                            hidden_products: [],
                            hidden_steps: [],
                            hidden_substeps: [],
                            action_type: "products",
                        });
                        setValue("product_conditions", updatedConditions, { shouldDirty: true });
                    }}
                >
                    + Add Conditional Logic
                </Button>
            </div>

            <CustomModal
                title={activeProductType === "included_products" ? "Included Products" : "Hidden Products"}
                open={showProductModal}
                onClose={() => setShowProductModal(false)}
                secondaryActions={[{ content: "Close", onAction: () => setShowProductModal(false) }]}
                primaryAction={{
                    content:
                        activeConditionIndex !== null &&
                        activeProductType &&
                        productConditions?.[activeConditionIndex]?.[activeProductType]?.length > 0
                            ? "Edit Products"
                            : "Add Products",
                    onAction: () => {
                        if (activeConditionIndex !== null && activeProductType)
                            handleProductSearch(activeConditionIndex, activeProductType);
                    },
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {activeConditionIndex !== null &&
                    activeProductType &&
                    productConditions?.[activeConditionIndex]?.[activeProductType]?.length > 0 ? (
                        productConditions[activeConditionIndex][activeProductType].map((product: ProductObj) => (
                            <div
                                key={product.id}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    borderBottom: "1px solid #eee",
                                }}
                            >
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <Thumbnail
                                        source={product?.images[0]?.originalSrc || ProductIcon}
                                        alt={product?.images[0]?.altText || ""}
                                        size="small"
                                    />
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                        <span>{product.title}</span>
                                        <span style={{ fontSize: "10px" }}>
                                            {product.variants.length} variants included
                                        </span>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => handleProductDelete(product.id)}
                                    icon={DeleteIcon}
                                    variant="plain"
                                    tone="critical"
                                    accessibilityLabel="Delete"
                                ></Button>
                            </div>
                        ))
                    ) : (
                        <p>No products added.</p>
                    )}
                </div>
            </CustomModal>
            <CustomModal
                title="Select Steps to Hide"
                open={showStepModal}
                onClose={() => setShowStepModal(false)}
                primaryAction={{ content: "Done", onAction: () => setShowStepModal(false) }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "10px" }}>
                    {steps.length > 0 ? (
                        steps.map((step, idx) => (
                            <div key={idx} style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <FormCheckbox
                                        name={`product_conditions.${activeConditionIndex ?? 0}.hidden_steps`}
                                        label={step.step_label || `Step ${idx + 1}`}
                                        checked={
                                            activeConditionIndex !== null &&
                                            productConditions[activeConditionIndex].hidden_steps?.includes(
                                                step.step_label,
                                            )
                                        }
                                        onChange={() => handleStepToggle(step.step_label)}
                                    />
                                </div>
                                {step.enable_subSteps && step.subSteps?.length > 0 && (
                                    <div
                                        style={{
                                            paddingLeft: "30px",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "5px",
                                        }}
                                    >
                                        {step.subSteps.map((subStep: any, subIdx: number) => (
                                            <div
                                                key={subIdx}
                                                style={{ display: "flex", alignItems: "center", gap: "10px" }}
                                            >
                                                <FormCheckbox
                                                    name={`product_conditions.${activeConditionIndex ?? 0}.hidden_substeps`}
                                                    label={subStep.step_label || `Substep ${subIdx + 1}`}
                                                    checked={
                                                        activeConditionIndex !== null &&
                                                        productConditions[
                                                            activeConditionIndex
                                                        ].hidden_substeps?.includes(
                                                            `${step.step_label}:${subStep.step_label}`,
                                                        )
                                                    }
                                                    onChange={() =>
                                                        handleStepToggle(step.step_label, subStep.step_label)
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <p>No steps found.</p>
                    )}
                </div>
            </CustomModal>
        </div>
    );
};

const ExemptProductsManager = ({ setValue, steps, sft }: { setValue: any; steps: StepsType[]; sft: string }) => {
    const [showExemptModal, setShowExemptModal] = useState(false);
    const [activeExemptStepIdx, setActiveExemptStepIdx] = useState<number | null>(null);
    const [activeExemptSubStepIdx, setActiveExemptSubStepIdx] = useState<number | null>(null);
    const [exemptProductsList, setExemptProductsList] = useState<any[]>([]);
    const [isFetchingExempt, setIsFetchingExempt] = useState(false);
    const stepsWithConditions = steps.filter(
        (step: any) => step.show_step_conditions || (step.enable_subSteps && step.substep_rules_type !== "no_rules"),
    );

    const openExemptModal = async (stepIdx: number, subStepIdx?: number) => {
        setActiveExemptStepIdx(stepIdx);
        setActiveExemptSubStepIdx(subStepIdx ?? null);
        setShowExemptModal(true);
        setIsFetchingExempt(true);

        const step = steps[stepIdx];
        const exemptIds =
            subStepIdx !== undefined ? step.subSteps[subStepIdx].exempt_product_ids : step.exempt_product_ids || [];

        if (exemptIds && exemptIds.length > 0) {
            try {
                const products = await getProductsDynamicallyAndReturnNewProductsArray(
                    exemptIds.map((id: string) => ({ id, variants: [] })),
                    sft,
                );
                setExemptProductsList(products);
            } catch (error) {
                console.error("Failed to fetch exempt products:", error);
            }
        } else {
            setExemptProductsList([]);
        }
        setIsFetchingExempt(false);
    };

    const handleExemptProductDelete = (productId: string) => {
        if (activeExemptStepIdx === null) return;

        const updatedSteps = [...steps];
        const stepIdx = activeExemptStepIdx;
        const subStepIdx = activeExemptSubStepIdx;

        if (subStepIdx !== null) {
            const currentList = updatedSteps[stepIdx].subSteps[subStepIdx].exempt_product_ids || [];
            updatedSteps[stepIdx].subSteps[subStepIdx] = {
                ...updatedSteps[stepIdx].subSteps[subStepIdx],
                exempt_product_ids: currentList.filter((id: string) => id !== productId),
            };
        } else {
            const currentList = updatedSteps[stepIdx].exempt_product_ids || [];
            updatedSteps[stepIdx] = {
                ...updatedSteps[stepIdx],
                exempt_product_ids: currentList.filter((id: string) => id !== productId),
            };
        }

        setValue("steps", updatedSteps, { shouldDirty: true });
        setExemptProductsList((prev) => prev.filter((p) => p.id !== productId));
    };

    const handleProductSearch = async (stepIdx: number, subStepIdx?: number) => {
        const step = steps[stepIdx];
        const subStep = subStepIdx !== undefined ? step.subSteps[subStepIdx] : null;
        const currentTarget: any = subStep || step;
        const currentList = currentTarget.exempt_product_ids || [];

        const preSelected = currentList?.map((id: string) => ({ id }));

        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            action: "select",
            selectionIds: preSelected,
            filter: { query: "status:active AND published_status:published", variants: false },
        });

        if (selected) {
            let finalProductIds = selected.map((p: any) => p.id);

            // Validation: Only allow products included in the step
            try {
                // Fetch full product details to check collection memberships
                const productsWithDetails = await getProductsDynamicallyAndReturnNewProductsArray(
                    selected.map((p: any) => ({ ...p, variants: [] })),
                    sft,
                );

                const allowedProductIds = currentTarget.products?.map((p: any) => p.id) || [];
                const allowedCollectionIds = currentTarget.collections?.map((c: any) => c.id) || [];

                const validProducts = productsWithDetails.filter((p: any) => {
                    const isDirectlyIncluded = allowedProductIds.includes(p.id);
                    const isInIncludedCollection = p?.collections?.some((collectionId: any) =>
                        allowedCollectionIds.includes(collectionId),
                    );
                    return isDirectlyIncluded || isInIncludedCollection;
                });

                if (validProducts.length !== selected.length) {
                    finalProductIds = validProducts.map((p: any) => p.id);
                    shopify.toast.show("Only products included in the bundle step can be selected", {
                        isError: true,
                        duration: 1500,
                    });
                }
            } catch (error) {
                console.error("Validation failed:", error);
            }

            const updatedSteps = [...steps];
            if (subStepIdx !== undefined) {
                updatedSteps[stepIdx].subSteps[subStepIdx] = {
                    ...updatedSteps[stepIdx].subSteps[subStepIdx],
                    exempt_product_ids: finalProductIds,
                };
            } else {
                updatedSteps[stepIdx] = { ...updatedSteps[stepIdx], exempt_product_ids: finalProductIds };
            }

            setValue("steps", updatedSteps, { shouldDirty: true });
        }
    };

    if (stepsWithConditions.length === 0) {
        return (
            <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                <Text as="p" tone="subdued">
                    No steps have quantity conditions enabled. Enable conditions in step settings to exempt products.
                </Text>
            </Box>
        );
    }

    return (
        <>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    padding: "10px",
                    borderRadius: "8px",
                    backgroundColor: "#f9f9f9",
                }}
            >
                <Text as="h3" variant="headingSm">
                    Exempt Products by Step
                </Text>
                {steps.map((step: any, sIdx: number) => {
                    if (
                        !(step.show_step_conditions || (step.enable_subSteps && step.substep_rules_type !== "no_rules"))
                    )
                        return null;

                    return (
                        <div
                            key={sIdx}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                border: "1px solid #ccc",
                                padding: "14px 28px 14px 14px",
                                borderRadius: "6px",
                                backgroundColor: "#fff",
                            }}
                        >
                            <Text as="p" fontWeight="bold">
                                {step.step_label || `Step ${sIdx + 1}`}
                            </Text>
                            {step.enable_subSteps ? (
                                <BlockStack gap="200">
                                    {step.subSteps.map((ss: any, ssIdx: number) => (
                                        <InlineStack key={ssIdx} align="space-between" blockAlign="center">
                                            <Text as="span">{ss.step_label || `Substep ${ssIdx + 1}`}</Text>
                                            <InlineStack gap="200" align="end" blockAlign="center">
                                                <Button onClick={() => handleProductSearch(sIdx, ssIdx)}>
                                                    Select Products
                                                </Button>
                                                <p
                                                    style={{
                                                        textDecoration: "underline",
                                                        cursor: "pointer",
                                                        color: "#007ace",
                                                        minWidth: "80px",
                                                    }}
                                                    onClick={() => openExemptModal(sIdx, ssIdx)}
                                                >
                                                    {ss.exempt_product_ids?.length || 0} selected
                                                </p>
                                            </InlineStack>
                                        </InlineStack>
                                    ))}
                                </BlockStack>
                            ) : (
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="span">Exempt from step conditions</Text>
                                    <InlineStack gap="200" align="end" blockAlign="center">
                                        <Button onClick={() => handleProductSearch(sIdx)}>Select Products</Button>
                                        <p
                                            style={{
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                                color: "#007ace",
                                                minWidth: "80px",
                                            }}
                                            onClick={() => openExemptModal(sIdx)}
                                        >
                                            {step.exempt_product_ids?.length || 0} selected
                                        </p>
                                    </InlineStack>
                                </InlineStack>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Modal for viewing/deleting exempt products */}
            <CustomModal
                title="Exempt Products"
                open={showExemptModal}
                onClose={() => setShowExemptModal(false)}
                primaryAction={{ content: "Done", onAction: () => setShowExemptModal(false) }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "10px" }}>
                    {isFetchingExempt ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                            <Spinner size="large" />
                        </div>
                    ) : exemptProductsList.length > 0 ? (
                        exemptProductsList.map((product: any) => (
                            <div
                                key={product.id}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "10px",
                                    padding: "8px",
                                    border: "1px solid #eee",
                                    borderRadius: "4px",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <Thumbnail
                                        source={product?.images?.[0]?.originalSrc || product?.image || ProductIcon}
                                        alt={product?.title || ""}
                                        size="small"
                                    />
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                        <Text as="span">{product.title}</Text>
                                        <Text as="span" variant="bodyXs" tone="subdued">
                                            {product.variants?.length || 0} variants
                                        </Text>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => handleExemptProductDelete(product.id)}
                                    icon={DeleteIcon}
                                    variant="plain"
                                    tone="critical"
                                    accessibilityLabel="Delete"
                                />
                            </div>
                        ))
                    ) : (
                        <Text as="p" tone="subdued">
                            No products added.
                        </Text>
                    )}
                </div>
            </CustomModal>
        </>
    );
};
